import React, { useEffect } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { ArrowLeft, X, Ban, Redo, Save, SquareMousePointer, Undo, Loader2, CircleQuestionMark } from 'lucide-react'
import { ViewerConfig } from "@/components/Viewer"
import { HostedViewerCtx, useHostedViewerCtx } from "@/hooks/use-app-ctx"
import { useSelector } from '@/store'
import { useSyncSignal } from '@/hooks/use-sync-signal'
import { useConfigMode } from '@/components/editor/configuration-mode'
import { UserMenu } from '@/components/editor/configuration-top-bar/user-menu'
import { ConfigurationModeSwitch } from '@/components/editor/configuration-top-bar/configuration-mode-switch'
import { TopBar, TopBarButton, TopBarSeparator } from '@/components/editor/configuration-top-bar/primitives'
import { useWidgetSelectorStore, WidgetSelectorHandle } from '@/components/editor/widget-selector/widget-selector'
import { mergeClasses } from '@/helpers/styling'
import { getViewerUndoRedo, useViewerUndoRedo } from '@/components/editor/widget-editor/undo-redo'
import { hasUnappliedInvalidEdits } from '@/components/editor/widget-editor/editor-controller'
import { isMacOS } from '@/utils/operating-system'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'

const DOCUMENTATION_URL = 'https://projects.wistor.nl/projects/wistor/documentation'

export function ConfigurationTopBar() {
  const viewerCtx = useHostedViewerCtx()
  const widgetSelector = viewerCtx.widgetSelectorRef.current
  const viewerConfig = useSelector(s => s.currentViewer?.config)

  if (!viewerConfig || !widgetSelector) return null

  return (
    <ConfigurationTopBarImpl
      viewerConfig={viewerConfig}
      viewerCtx={viewerCtx}
      widgetSelector={widgetSelector}
      />
  )
}

function isEditableTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return false

  return (
    target.tagName === "INPUT" ||
    target.tagName === "TEXTAREA" ||
    target.isContentEditable
  )
}

function ConfigurationTopBarImpl(props: {
  viewerConfig: ViewerConfig
  viewerCtx: HostedViewerCtx,
  widgetSelector: WidgetSelectorHandle
}) {
  const { viewerConfig, viewerCtx, widgetSelector } = props
  const projectPath = viewerCtx.viewProps.projectPath
  const mode = useConfigMode()

  const startOrStopPicking = () => {
    // @ts-ignore
    window.winsel = widgetSelector
    if (widgetSelector.started()) widgetSelector.cancel()
    else widgetSelector.start()
  }

  const deselect = () => {
    useWidgetSelectorStore.setState({ selectedPath: null })
  }

  const isPicking = useSyncSignal(widgetSelector.startedSignal, widgetSelector.started())
  const selectedWidgetPath = useWidgetSelectorStore(s => s.selectedPath)

  const [ viewerSnapshot, undoRedoControls, prevDescription, nextDescription ] = useViewerUndoRedo()
  const canUndo = viewerSnapshot && undoRedoControls?.canBack()
  const canRedo = viewerSnapshot && undoRedoControls?.canForward()
  const hasUnsaved = undoRedoControls && undoRedoControls.getHistory().length > 1

  const saveMutation = useMutation({
    mutationFn: async () => {
      try {
        await viewerCtx.save()
        toast.info("App configuration saved.")
      } catch (e) {
        toast.error(`Failed to save app configuration: ${String(e)}.`)
      }
    }
  })

  useListenToKeyboardShortcuts(widgetSelector, () => saveMutation.mutate())
  usePreventUnloadWithUnsavedChanges()

  return (
    <TopBar>
      <TopBarButton title="Browse projects" to={`/projects/${projectPath}`}>
        <ArrowLeft size={18} />
      </TopBarButton>
      <ConfigurationModeSwitch />
      <div className="flex flex-grow p-1 min-w-0 gap-1.5 justify-start opacity-75 text-[14px]">
        <Link to={`/projects/${projectPath}`}
              className="flex-[0_4_auto] min-w-0 opacity-75 truncate hover:text-white">
          {viewerConfig.project?.title}
        </Link>
        <span className="opacity-50">/</span>
        <span className="flex-[1_3_auto] min-w-0 font-medium truncate">{viewerConfig.title}</span>
      </div>
      {mode === "edit" && (
        <>
          <div className="flex gap-1.5 mr-1">
            {(selectedWidgetPath && !isPicking) && (
              <TopBarButton title="Deselect widget [Esc]"
                            variant="ghost"
                            onClick={deselect}>
                <X size={18} />
              </TopBarButton>
            )}
            <TopBarButton title={"Pick a widget\n[Q] to start, [Esc] to cancel"}
                          className={mergeClasses({
                            "ring-2 animate-pulse": isPicking
                          })}
                          variant="outline"
                          onClick={startOrStopPicking}>
              <SquareMousePointer size={18} />
              {isPicking ? 'Picking widget…' : 'Pick widget'}
            </TopBarButton>
            <TopBarSeparator />
            <TopBarButton title="Save configuration [Ctrl+S]"
                          variant={hasUnsaved ? "purple" : "outline"}
                          disabled={!hasUnsaved}
                          onClick={saveMutation.mutate}>
              {saveMutation.isPending ? (
                <Loader2 className="animate-spin" size={18} />
              ) : (
                <Save size={18} />
              )}
              Save changes
            </TopBarButton>
            <TopBarButton title={`Undo ${prevDescription} (Ctrl+Z)`}
                          disabled={!canUndo}
                          onClick={() => canUndo && undoRedoControls?.back()}>
              <Undo size={18} />
            </TopBarButton>
            <TopBarButton title={`Redo ${nextDescription} (Ctrl+Shift+Z)`}
                          disabled={!canRedo}
                          onClick={() => canRedo && undoRedoControls?.forward()}>
              <Redo size={18} />
            </TopBarButton>
          </div>
        </>
      )}
      <TopBarButton title="Wistor documentation" href={DOCUMENTATION_URL}>
        <CircleQuestionMark size={18} />
      </TopBarButton>
      <UserMenu />
    </TopBar>
  )
}

function useListenToKeyboardShortcuts(widgetSelector: WidgetSelectorHandle, saveChanges: () => void) {
  useEffect(() => {
    const controller = new AbortController()
    window.addEventListener("keydown", (e) => {
      const isEditing = isEditableTarget(e.target)

      const ctrlOrMeta = ((isMacOS && e.metaKey) || e.ctrlKey)

      const undoRedo = getViewerUndoRedo()

      if (isEditing) return

      if (ctrlOrMeta && e.key.toLowerCase() === "z") {
        if (undoRedo) {
          if (e.shiftKey) {
            undoRedo.forward()
          } else {
            undoRedo.back()
          }
        }
      }

      if (e.key.toLowerCase() === "q") {
        widgetSelector.start()
      }

      if (e.key.toLowerCase() === "escape") {
        if (widgetSelector.started()) {
          widgetSelector.cancel()
        } else {
          useWidgetSelectorStore.setState({ selectedPath: null })
        }
      }

      if (ctrlOrMeta && e.key.toLowerCase() === "s") {
        e.preventDefault()
        saveChanges()
      }
    }, { signal: controller.signal })

    return () => controller.abort()
  }, [])
}

function usePreventUnloadWithUnsavedChanges() {
  const history = useHistory()

  useEffect(() => {
    const controller = new AbortController()
    
    window.addEventListener('beforeunload', (e: BeforeUnloadEvent) => {
      if (!hasUnsavedChanges()) return
      
      e.preventDefault()
      // noinspection JSDeprecatedSymbols
      e.returnValue = ''
    }, { signal: controller.signal })

    return () => { controller.abort() }
  }, [])

  // beforeunload does not fire for in-app navigation (links, the browser's back button), so block those too
  useEffect(() => history.block(location => {
    if (!hasUnsavedChanges()) return
    // history.location is still the page being left; on back/forward window.location already shows the target
    if (appBasePath(location.pathname) === appBasePath(history.location.pathname)) return // switching edit/debug/preview
    return "You have unsaved changes in this app. Leave anyway? Your changes will be lost."
  }), [ history ])
}

function hasUnsavedChanges() {
  const undoRedo = getViewerUndoRedo()
  return Boolean(undoRedo && undoRedo.getHistory().length > 1) || hasUnappliedInvalidEdits()
}

function appBasePath(pathname: string) {
  return pathname.replace(/\/(edit|debug)?\/?$/, '')
}
